import React from 'react';
import { useFormContext } from 'react-hook-form';
import Collapse from '@mui/material/Collapse';
import classNames from 'classnames';
import ShowHideToggle from '@/components/common/ShowHideToggleIcon';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { useSortedCustomFields } from '@/modules/Settings';
import CustomFieldInput from './CustomFieldInput';

function CustomFieldFilters({
  className = '',
  allowShowMore = true,
  fieldPrefix = '',
  fieldSuffix = '',
  hasAdvancedSearch = false,
  isSlug = false,
}: {
  className?: string;
  allowShowMore?: boolean;
  fieldPrefix?: string;
  fieldSuffix?: string;
  hasAdvancedSearch?: boolean;
  isSlug?: boolean;
}) {
  const { getValues, formState } = useFormContext();
  const { noCustomFields, sortedCustomFields } = useSortedCustomFields(
    !hasAdvancedSearch,
  );

  const hasCustomFieldValues = React.useMemo(() => {
    const customFieldSlugs: string[] = [];
    sortedCustomFields.forEach(({ customFields }) => {
      customFields.forEach(({ slug }) =>
        customFieldSlugs.push(fieldPrefix + slug),
      );
    });
    const formValues = getValues(customFieldSlugs);
    return formValues.some((value) =>
      Array.isArray(value)
        ? value.length
        : value !== undefined && value !== null && value !== '',
    );
  }, [getValues, sortedCustomFields]);
  const [showMore, setShowMore] = React.useState(hasCustomFieldValues);
  React.useEffect(() => {
    if (hasCustomFieldValues && !formState.isDirty) {
      setShowMore(true);
    }
  }, [hasCustomFieldValues, formState, setShowMore]);

  const CustomFields = React.useMemo(
    () => (
      <div className={classNames('col-span-full grid gap-4', className)}>
        {sortedCustomFields.map(({ customFields, label, value }) =>
          customFields.length ? (
            <React.Fragment key={value}>
              <div className="mt-3 type-body4">
                {translate('CUSTOM_FIELDS')} - {label}
              </div>
              {customFields.map(({ slug, ...field }) => (
                <CustomFieldInput
                  key={field.customFieldId}
                  slug={
                    fieldPrefix +
                    (isSlug ? slug : field.customFieldId) +
                    fieldSuffix
                  }
                  {...field}
                />
              ))}
            </React.Fragment>
          ) : null,
        )}
      </div>
    ),
    [className, fieldPrefix, sortedCustomFields],
  );
  if (!allowShowMore) {
    return CustomFields;
  }

  return noCustomFields ? null : (
    <>
      <Collapse in={showMore}>{CustomFields}</Collapse>
      <div className="col-span-full">
        <button
          onClick={() => setShowMore(!showMore)}
          type="button"
          className="py-3"
        >
          <ShowHideToggle
            isOpen={showMore}
            hideText={translate('HIDE_CUSTOM_FIELDS')}
            showText={translate('SHOW_CUSTOM_FIELDS')}
          />
        </button>
      </div>
    </>
  );
}

export default CustomFieldFilters;
